"use client";

import * as React from "react";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";

import { cn } from "#/utils";

export type TooltipProviderProps = React.ComponentProps<
  typeof TooltipPrimitive.Provider
> & {
  delayDuration?: number;
  skipDelayDuration?: number;
};
function TooltipProvider({
  delayDuration,
  skipDelayDuration,
  delay,
  timeout,
  ...props
}: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider
      delay={delay ?? delayDuration}
      timeout={timeout ?? skipDelayDuration}
      {...props}
    />
  );
}

export type TooltipProps = React.ComponentProps<
  typeof TooltipPrimitive.Root
> & {
  delayDuration?: number;
};
function Tooltip({ delayDuration: _delayDuration, ...props }: TooltipProps) {
  return <TooltipPrimitive.Root {...props} />;
}

export type TooltipTriggerElement = HTMLButtonElement;
export type TooltipTriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
> & {
  asChild?: boolean;
};
const TooltipTrigger = React.forwardRef<
  TooltipTriggerElement,
  TooltipTriggerProps
>(({ asChild, children, render, ...props }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return (
      <TooltipPrimitive.Trigger
        ref={ref}
        render={children as React.ReactElement}
        {...props}
      />
    );
  }
  return (
    <TooltipPrimitive.Trigger ref={ref} render={render} {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

export type TooltipContentElement = React.ElementRef<
  typeof TooltipPrimitive.Popup
>;
export type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Popup
> & {
  align?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["align"];
  side?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["side"];
  sideOffset?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["sideOffset"];
  alignOffset?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["alignOffset"];
  collisionPadding?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["collisionPadding"];
  collisionBoundary?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["collisionBoundary"];
  sticky?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Positioner
  >["sticky"];
  forceMount?: boolean;
};
const TooltipContent = React.forwardRef<
  TooltipContentElement,
  TooltipContentProps
>(
  (
    {
      className,
      align = "center",
      side = "top",
      sideOffset = 4,
      alignOffset = 0,
      collisionPadding,
      collisionBoundary,
      sticky,
      forceMount,
      hidden,
      children,
      ...props
    },
    ref,
  ) => {
    if (hidden) return null;
    return (
      <TooltipPrimitive.Portal keepMounted={forceMount}>
        <TooltipPrimitive.Positioner
          align={align}
          side={side}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={collisionPadding}
          collisionBoundary={collisionBoundary}
          sticky={sticky}
          className="isolate z-[var(--reopt-z-portal,60)]"
        >
          <TooltipPrimitive.Popup
            ref={ref}
            className={cn(
              "relative z-[var(--reopt-z-portal,60)] inline-flex w-fit max-w-xs items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
              "data-[open]:animate-in data-[open]:fade-in-0 data-[open]:zoom-in-95",
              "data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95",
              className,
            )}
            {...props}
          >
            {children}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    );
  },
);
TooltipContent.displayName = "TooltipContent";

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
